import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import LandingPage from './LandingPage';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import TreasuryModal from './components/TreasuryModal';
import JournalBook from './components/JournalBook';
import Planner from './components/Planner';
import Codex from './components/Codex';
import Calendar from './components/Calendar';
import DashboardCore from './components/DashboardCore';

import { DialogProvider, useDialog } from './DialogContext'; 

// --- SUPABASE AND COMPONENT IMPORTS ---
import { supabase } from './supabaseClient';
import { AuthProvider, useAuth } from './AuthContext';
import AuthGate from './AuthGate';

const ICONS = {
  intel: "📜",
  disc: "⚔️",
  energy: "🔥",
  wealth: "⚜️",
  vision: "🕯️",
  shield: "🛡️",
  high: "♦️", medium: "🔸", low: "🔹", delete: "✕"
};

const AUDIO_TRACKS = {
  rain: process.env.PUBLIC_URL + "/sounds/rain.mp3",
  fire: process.env.PUBLIC_URL + "/sounds/fire.mp3",
  cafe: process.env.PUBLIC_URL + "/sounds/new_age.mp3" 
};

const defaultState = {
  name: "", 
  level: 1, xp: 0, xpToNext: 100,
  stats: { intelligence: 10, discipline: 10, vision: 10, energy: 10, wealth: 10 }, 
  multipliers: null, 
  habits: [], activeQuests: [], archive: [], inventory: [],
  customRewards: [], 
  lastLogin: new Date().toDateString(),
  notes: "",
  journal: [], backlog: [],
  avatarUrl: null, 
  calendarEvents: [],
};

// --- DRAG AND DROP COMPONENT ---
const DraggableRankList = ({ ranks, setRanks }) => {
  const [draggedIdx, setDraggedIdx] = useState(null);
  const rankLabels = ["1st (1.5x)", "2nd (1.25x)", "3rd (1.0x)", "4th (0.75x)", "5th (0.5x)"];

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === idx) return;
    const newRanks = [...ranks];
    const item = newRanks.splice(draggedIdx, 1)[0];
    newRanks.splice(idx, 0, item);
    setRanks(newRanks);
    setDraggedIdx(null);
  };

  return (
    <div style={{width: '100%', display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '15px', marginBottom: '15px'}}>
      {ranks.map((stat, idx) => (
        <div 
          key={stat}
          draggable
          onDragStart={() => setDraggedIdx(idx)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => handleDrop(e, idx)}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 15px', border: draggedIdx === idx ? '1px solid var(--accent-gold)' : '1px solid var(--faded-line)', 
            backgroundColor: draggedIdx === idx ? 'rgba(212, 175, 55, 0.1)' : 'rgba(0,0,0,0.3)',
            cursor: 'grab', color: 'var(--ink-color)', fontFamily: 'Courier Prime, monospace', borderRadius: '4px',
            transition: 'all 0.2s', boxShadow: draggedIdx === idx ? '0 5px 15px rgba(0,0,0,0.5)' : 'none'
          }}
        >
          <span style={{color: 'var(--muted-gold)', fontSize: '0.85rem', width: '80px', display: 'flex', flexDirection: 'column', lineHeight: '1.2'}}>
            <span>{rankLabels[idx].split(' ')[0]}</span>
            <span style={{fontSize: '0.7rem', opacity: 0.7}}>{rankLabels[idx].split(' ')[1]}</span>
          </span>
          <span style={{textTransform: 'capitalize', flex: 1, textAlign: 'center', fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px'}}>
            {stat} {ICONS[stat === 'intelligence' ? 'intel' : stat === 'discipline' ? 'disc' : stat]}
          </span>
          <span style={{opacity: 0.5, fontSize: '1.2rem', paddingLeft: '10px'}}>☰</span>
        </div>
      ))}
    </div>
  );
};

// --- MAIN DASHBOARD COMPONENT ---
const Dashboard = () => {
  const { user, signOut } = useAuth();
  const { showConfirm, showAlert } = useDialog();

  const [loading, setLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false); 
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showShop, setShowShop] = useState(false);
  const [showSettings, setShowSettings] = useState(false); 
  const [journalPageIndex, setJournalPageIndex] = useState(0);
  const audioRef = useRef(null);
  const [audioState, setAudioState] = useState({ playing: false, currentTrack: 'rain' });
  const [player, setPlayer] = useState(defaultState);

  const [onboardingName, setOnboardingName] = useState(""); 
  const [onboardingRanks, setOnboardingRanks] = useState(["vision", "intelligence", "discipline", "energy", "wealth"]);
  const [settingsRanks, setSettingsRanks] = useState(["vision", "intelligence", "discipline", "energy", "wealth"]);

  const [newPassword, setNewPassword] = useState("");
  const [showPasswordInput, setShowPasswordInput] = useState(false); 
  const [wipeConfirmation, setWipeConfirmation] = useState(""); 

  const [inputs, setInputs] = useState({
    habitName: "", journalEntry: "", backlogItem: "" 
  });

  const playTrack = (trackName) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audioState.playing && audioState.currentTrack === trackName) {
      audio.pause(); setAudioState({ ...audioState, playing: false });
    } else {
      audio.src = AUDIO_TRACKS[trackName]; audio.load(); audio.loop = true; audio.volume = 0.5;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.then(() => setAudioState({ playing: true, currentTrack: trackName }))
        .catch(error => console.error("Audio error:", error));
      }
    }
  };

  useEffect(() => {
    const fetchPlayerData = async () => {
      if (!user || !user.id) return; 
      
      try {
        const { data, error } = await supabase.from('player_data').select('*').eq('id', user.id).single();

        if (error) {
          if (error.code === 'PGRST116') {
            setPlayer(defaultState);
            setIsDataLoaded(true); 
            return;
          }
          throw error;
        }

        if (data) {
          const safeData = {
            ...defaultState, ...data,
            avatarUrl: data.avatarUrl || null, 
            stats: { ...defaultState.stats, ...(data.stats || {}) },
            conscienceSettings: { ...defaultState.conscienceSettings, ...(data.conscienceSettings || {}) },
            activeQuests: data.activeQuests || [], habits: data.habits || [], archive: data.archive || [],
            inventory: data.inventory || [], journal: data.journal || [], backlog: data.backlog || [],
            customRewards: data.customRewards || [], 
            historyGraph: data.historyGraph || [...defaultState.historyGraph],
            calendarEvents: data.calendarEvents || []
          };

          const today = new Date().toDateString();
          if (safeData.lastLogin !== today) {
            safeData.habits = safeData.habits.map(h => ({ ...h, completed: false, completedDates: h.completedDates || [] }));
            safeData.historyGraph.shift(); 
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            safeData.historyGraph.push({ day: dayNames[new Date().getDay()], xp: 0 });
            safeData.lastLogin = today;
          }
          
          setPlayer(safeData);
          setIsDataLoaded(true); 

          if (safeData.multipliers) {
            const sortedStats = Object.keys(safeData.multipliers).sort((a, b) => safeData.multipliers[b] - safeData.multipliers[a]);
            setSettingsRanks(sortedStats.length === 5 ? sortedStats : ["vision", "intelligence", "discipline", "energy", "wealth"]);
          }
        }
      } catch (err) {
        setPlayer(defaultState); setIsDataLoaded(true); 
      } finally { setLoading(false); }
    };

    fetchPlayerData();
  }, [user]);

  useEffect(() => {
    if (!loading && isDataLoaded && user) {
      const saveToCloud = async () => {
        try {
          const payload = { ...player, id: user.id }; 
          const { error } = await supabase.from('player_data').upsert(payload); 
          if (error) {
             console.error("Auto-Save Error:", error);
             if(error.code === '22001') showAlert("Photo is too large. Please select a file under 5MB.", "Upload Error");
          }
        } catch (err) { console.error("Auto-Save Error Details:", err); }
      };
      const timer = setTimeout(() => { saveToCloud(); }, 1500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, loading, isDataLoaded, user]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;
    
    if (file.size > 5 * 1024 * 1024) {
      showAlert("Photo is too large. Please select a file under 5MB.", "Upload Error");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = 200; 
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        
        const minDim = Math.min(img.width, img.height);
        const startX = (img.width - minDim) / 2;
        const startY = (img.height - minDim) / 2;
        ctx.drawImage(img, startX, startY, minDim, minDim, 0, 0, size, size);
        
        canvas.toBlob(async (blob) => {
          try {
            const fileName = `${user.id}-avatar.jpg`;
            const { error: uploadError } = await supabase.storage
              .from('avatars')
              .upload(fileName, blob, {
                contentType: 'image/jpeg',
                upsert: true 
              });

            if (uploadError) throw uploadError;

            const { data } = supabase.storage.from('avatars').getPublicUrl(fileName);
            const publicUrl = `${data.publicUrl}?t=${Date.now()}`;
            
            setPlayer(prev => ({ ...prev, avatarUrl: publicUrl }));
            showAlert("Your photo has been successfully sealed in the Cloud!", "Cloud Sync");

          } catch (err) {
            console.error("Storage Upload Error:", err);
            showAlert("An error occurred while uploading the photo: " + err.message, "Upload Error");
          }
        }, 'image/jpeg', 0.8);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const addXP = (amount) => {
    let newXp = player.xp + amount;
    let newLevel = player.level;
    let newThreshold = player.xpToNext;
    const newGraph = player.historyGraph ? [...player.historyGraph] : [...defaultState.historyGraph];
    
    if (newXp < 0) newXp = 0; 

    const todayIndex = newGraph.length - 1;
    if(newGraph[todayIndex]) {
      let updatedGraphXp = newGraph[todayIndex].xp + amount;
      if (updatedGraphXp < 0) updatedGraphXp = 0; 
      newGraph[todayIndex] = { ...newGraph[todayIndex], xp: updatedGraphXp };
    }

    if (newXp >= newThreshold) {
      newXp -= newThreshold; newLevel++; newThreshold = Math.floor(newThreshold * 1.2);
      showAlert(`Welcome to Level ${newLevel}. Your journey continues.`, "Level Up ⚔️");
    }
    return { xp: newXp, level: newLevel, xpToNext: newThreshold, graph: newGraph };
  };

  const handleNewTask = (newTask) => setPlayer(prev => ({ ...prev, activeQuests: [...(prev.activeQuests || []), newTask] }));

  const completeQuest = (id) => {
    const quest = player.activeQuests?.find(q => q.id === id);
    if (!quest) return;
    const multiplier = player.multipliers ? player.multipliers[quest.stat] : 1;
    const totalXP = Math.floor((quest.val * 2) * multiplier);
    const res = addXP(totalXP);
    setPlayer(prev => ({
      ...prev, level: res.level, xp: res.xp, xpToNext: res.xpToNext, historyGraph: res.graph,
      stats: { ...prev.stats, [quest.stat]: prev.stats[quest.stat] + quest.val },
      activeQuests: prev.activeQuests.filter(q => q.id !== id),
      archive: [{...quest, date: new Date().toLocaleDateString()}, ...(prev.archive || [])].slice(0, 10)
    }));
  };

  const updateQuest = (id, newText) => {
    setPlayer(prev => ({ ...prev, activeQuests: prev.activeQuests.map(q => q.id === id ? { ...q, text: newText } : q) }));
  };

  const deleteHabit = (id) => {
    setPlayer(prev => ({ ...prev, habits: prev.habits.filter(h => h.id !== id) }));
  };

  const updateHabit = (id, newText) => {
    setPlayer(prev => ({ ...prev, habits: prev.habits.map(h => h.id === id ? { ...h, text: newText } : h) }));
  };

  const updateBacklog = (id, newText) => {
    setPlayer(prev => ({ ...prev, backlog: prev.backlog.map(b => b.id === id ? { ...b, text: newText } : b) }));
  };

  const addHabit = () => {
    if (!inputs.habitName) return;
    setPlayer(prev => ({ ...prev, habits: [...(prev.habits || []), { id: Date.now(), text: inputs.habitName, completed: false, completedDates: [] }] }));
    setInputs(prev => ({ ...prev, habitName: "" }));
  };

  const toggleHabit = (id, forceComplete = false) => {
    setPlayer(prevPlayer => {
        const habit = prevPlayer.habits?.find(h => h.id === id);
        if (!habit) return prevPlayer;

        const todayStr = new Date().toDateString();
        let currentCompletedDates = Array.isArray(habit.completedDates) ? [...habit.completedDates] : [];
        
        if (habit.completed && currentCompletedDates.length === 0) {
            currentCompletedDates.push(todayStr);
        }

        const isCurrentlyCompletedToday = currentCompletedDates.includes(todayStr);
        
        if (forceComplete && isCurrentlyCompletedToday) return prevPlayer;

        const xpChange = isCurrentlyCompletedToday ? -2 : 2;

        let newCompletedDates;
        if (isCurrentlyCompletedToday) {
             newCompletedDates = currentCompletedDates.filter(d => d !== todayStr);
        } else {
             newCompletedDates = [...currentCompletedDates, todayStr];
        }

        let newXp = prevPlayer.xp + xpChange;
        let newLevel = prevPlayer.level;
        let newThreshold = prevPlayer.xpToNext;
        const newGraph = prevPlayer.historyGraph ? [...prevPlayer.historyGraph] : [...defaultState.historyGraph];
        
        if (newXp < 0) newXp = 0; 
    
        const todayIndex = newGraph.length - 1;
        if(newGraph[todayIndex]) {
          let updatedGraphXp = newGraph[todayIndex].xp + xpChange;
          if (updatedGraphXp < 0) updatedGraphXp = 0; 
          newGraph[todayIndex] = { ...newGraph[todayIndex], xp: updatedGraphXp };
        }
    
        if (newXp >= newThreshold) {
          newXp -= newThreshold; newLevel++; newThreshold = Math.floor(newThreshold * 1.2);
          if (xpChange > 0) showAlert(`Welcome to Level ${newLevel}. Your journey continues.`, "Level Up ⚔️"); 
        }

        return {
            ...prevPlayer,
            level: newLevel,
            xp: newXp,
            xpToNext: newThreshold,
            historyGraph: newGraph,
            habits: prevPlayer.habits.map(h => 
                h.id === id 
                ? { ...h, completed: !isCurrentlyCompletedToday, completedDates: newCompletedDates } 
                : h
            )
        };
    });
  };

  const addBacklog = () => {
    if (!inputs.backlogItem) return;
    setPlayer(prev => ({ ...prev, backlog: [...(prev.backlog || []), { id: Date.now(), text: inputs.backlogItem }] }));
    setInputs(prev => ({ ...prev, backlogItem: "" }));
  };

  const promoteBacklog = (id, specificNewId = null) => {
    const item = player.backlog?.find(b => b.id === id); if (!item) return null;
    const newId = specificNewId || Date.now();
    setPlayer(prev => ({
      ...prev, backlog: prev.backlog.filter(b => b.id !== id),
      activeQuests: [...(prev.activeQuests || []), { id: newId, text: item.text, stat: 'discipline', priority: 'medium', val: 10 }]
    }));
    return newId;
  };

  const buyItem = (itemName, cost) => {
    if (player.stats.wealth >= cost) {
      showConfirm(`Exchange ${cost} Wealth for ${itemName}?`, () => {
        setPlayer(prev => ({
          ...prev, stats: { ...prev.stats, wealth: prev.stats.wealth - cost },
          inventory: [...(prev.inventory || []), { id: Date.now(), text: itemName }]
        }));
        showAlert(`${itemName} has been added to your inventory.`, "Treasury");
      }, "Confirm Exchange");
    } else { 
      showAlert("Not enough Wealth. Return when you have gathered more.", "Treasury Warning"); 
    }
  };

  const saveOnboarding = () => {
    if (!onboardingName.trim()) { showAlert("Please enter a name to begin your journey.", "Identity Required"); return; }
    const newMultipliers = {
      [onboardingRanks[0]]: 1.5, [onboardingRanks[1]]: 1.25, [onboardingRanks[2]]: 1.0, [onboardingRanks[3]]: 0.75, [onboardingRanks[4]]: 0.5
    };
    setPlayer(prev => ({ ...prev, name: onboardingName, multipliers: newMultipliers }));
  };

  const saveSettingsPriorities = () => {
    const updatedMultipliers = {
      [settingsRanks[0]]: 1.5, [settingsRanks[1]]: 1.25, [settingsRanks[2]]: 1.0, [settingsRanks[3]]: 0.75, [settingsRanks[4]]: 0.5
    };
    setPlayer(prev => ({ ...prev, multipliers: updatedMultipliers }));
    showAlert("Priorities have been successfully recalibrated.", "System Updated");
    setShowSettings(false);
  };

  const handlePasswordUpdate = async (passwordInput) => {
    if(passwordInput.length < 6) {
      showAlert("Password must be at least 6 characters.", "Security Alert");
      return;
    }
    const { error } = await supabase.auth.updateUser({ password: passwordInput });
    if (error) {
      showAlert("Error updating password: " + error.message, "Security Alert");
    } else {
      showAlert("Your vault's password has been successfully updated.", "Security Vault");
      setNewPassword("");
      setShowPasswordInput(false); 
    }
  };

  if (loading) {
    return (
      <div style={{height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#1a0f0a', color: '#d4af37', fontFamily: 'Courier Prime'}}>
        <h1 style={{animation: 'pulse 1.5s infinite'}}>CONNECTING TO THE CLOUD...</h1>
        <p>Syncing Madrearcal Database</p>
      </div>
    );
  }

  if (!player.multipliers) {
    return (
      <div className="modal-overlay" style={{backgroundColor: '#1a0f0a', zIndex: 999999}}>
        <div className="modal-content paper-panel" style={{maxWidth: '600px', textAlign: 'center'}}>
          <h1 style={{fontFamily: 'Cinzel', color: '#8b3a3a'}}>The Sorting Hat</h1>
          <p style={{marginBottom: '10px'}}>Who are you, and what do you need most right now?</p>
          
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            <input 
              className="vintage-input" 
              placeholder="Enter your Avatar Name..." 
              value={onboardingName} 
              onChange={e => setOnboardingName(e.target.value)}
              style={{width: '100%', maxWidth: '300px', textAlign: 'center', fontSize: '1.2rem', fontWeight: 'bold'}}
            />

            <div style={{width: '100%', borderTop: '1px solid #c5a059', margin: '20px 0', opacity: 0.3}}></div>
            
            <p style={{fontSize: '0.85rem', color: '#c5a059', marginBottom: '5px'}}>Drag and Drop to Re-Rank Your Priorities</p>
            <DraggableRankList ranks={onboardingRanks} setRanks={setOnboardingRanks} />

          </div>
          
          <button className="wax-seal-btn" style={{marginTop: '20px', padding: '10px 30px', borderRadius: '0'}} onClick={saveOnboarding}>LOCK DESTINY</button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <audio ref={audioRef} loop />

      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} audioState={audioState} playTrack={playTrack} setShowShop={setShowShop} signOut={signOut} player={player} />
      
      <main className="content-area">
        
        <TopBar player={player} setShowSettings={setShowSettings} />

        {activeTab === 'dashboard' && (
          <DashboardCore 
            player={player} setPlayer={setPlayer} handleNewTask={handleNewTask} completeQuest={completeQuest} 
            inputs={inputs} setInputs={setInputs} addHabit={addHabit} toggleHabit={toggleHabit} ICONS={ICONS} 
            updateQuest={updateQuest} deleteHabit={deleteHabit} updateHabit={updateHabit}
          />
        )}
        
        {activeTab === 'planner' && (
          <Planner 
            player={player} setPlayer={setPlayer} inputs={inputs} setInputs={setInputs} 
            addBacklog={addBacklog} promoteBacklog={promoteBacklog} updateBacklog={updateBacklog}
          />
        )}
        
        {activeTab === 'journal' && (
          <JournalBook 
            player={player} 
            setPlayer={setPlayer} 
            journalPageIndex={journalPageIndex} 
            setJournalPageIndex={setJournalPageIndex} 
            inputs={inputs} 
            setInputs={setInputs} 
          />
        )}
        
        {activeTab === 'codex' && (
          <Codex 
            player={player} 
            setPlayer={setPlayer} 
          />
        )}

        {activeTab === 'calendar' && (
          <Calendar 
            player={player}
            setPlayer={setPlayer}
            completeQuest={completeQuest}
            promoteBacklog={promoteBacklog}
            toggleHabit={toggleHabit}
           />
        )}

        {showShop && (
          <TreasuryModal 
            player={player}
            setPlayer={setPlayer} 
            setShowShop={setShowShop} 
            buyItem={buyItem} 
          />
        )}

        {/* YENİ NESİL PREMIUM SETTINGS MODAL */}
        {showSettings && (
          <div className="modal-overlay" onClick={() => setShowSettings(false)} style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 1000 }}>
            
            <div className="modal-content paper-panel" onClick={(e) => e.stopPropagation()} style={{ width: '850px', maxWidth: '95%', maxHeight: '90vh', overflowY: 'auto', position: 'relative', padding: '40px', border: '1px solid var(--accent-gold)' }}>
              
              <button 
                onClick={() => setShowSettings(false)} 
                style={{ position: 'absolute', top: '20px', right: '25px', background: 'none', border: 'none', color: 'var(--muted-gold)', fontSize: '1.5rem', cursor: 'pointer', transition: '0.2s' }} 
                onMouseOver={(e) => e.currentTarget.style.color = 'var(--accent-red)'} 
                onMouseOut={(e) => e.currentTarget.style.color = 'var(--muted-gold)'}
              >
                ✕
              </button>

              <h2 style={{ fontFamily: 'Cinzel, serif', color: 'var(--accent-gold)', margin: '0 0 35px 0', fontSize: '2rem', letterSpacing: '3px', textTransform: 'uppercase', textAlign: 'center', textShadow: '1px 1px 5px rgba(0,0,0,0.5)' }}>
                ⚙️ System Configuration
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '50px' }}>
                
                {/* SOL KOLON: KİMLİK VE GÜVENLİK */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '35px' }}>
                  
                  {/* IDENTITY SECTION */}
                  <div>
                    <h4 style={{ color: 'var(--accent-gold)', marginBottom: '15px', fontSize: '1.1rem', fontFamily: 'Cinzel, serif', borderBottom: '1px dashed var(--muted-gold)', paddingBottom: '8px' }}>Identity</h4>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', backgroundColor: 'rgba(0,0,0,0.2)', padding: '20px', borderRadius: '4px', border: '1px solid var(--faded-line)', boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.3)' }}>
                      <div 
                        title="Click to change photo"
                        onClick={() => document.getElementById('avatarUpload').click()}
                        style={{
                          width: '90px', height: '90px', borderRadius: '50%', backgroundColor: 'var(--bg-top)',
                          border: '2px solid var(--accent-gold)', display: 'flex', justifyContent: 'center', alignItems: 'center',
                          overflow: 'hidden', position: 'relative', cursor: 'pointer', flexShrink: 0, boxShadow: '0 4px 10px rgba(0,0,0,0.5)'
                        }}
                      >
                        {player.avatarUrl ? (
                          <img src={player.avatarUrl} alt="Avatar" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                        ) : (
                          <span style={{color: 'var(--accent-gold)', fontSize: '2.5rem', fontFamily: 'Cinzel, serif'}}>
                            {player.name ? player.name.charAt(0).toUpperCase() : 'M'}
                          </span>
                        )}
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.7)', color: 'var(--accent-gold)', fontSize: '0.65rem', textAlign: 'center', padding: '4px 0', letterSpacing: '1px', fontWeight: 'bold' }}>
                          EDIT
                        </div>
                      </div>
                      
                      <div style={{flex: 1}}>
                        <label style={{display: 'block', fontSize: '0.85rem', color: 'var(--muted-gold)', fontFamily: 'Courier Prime, monospace', marginBottom: '8px'}}>Avatar Name</label>
                        <input className="vintage-input" value={player.name} onChange={e => setPlayer({...player, name: e.target.value})} style={{width: '100%', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid var(--faded-line)', padding: '12px', color: '#fff', borderRadius: '4px'}} />
                        <input type="file" id="avatarUpload" accept="image/*" style={{display: 'none'}} onChange={handleImageUpload} />
                      </div>
                    </div>
                  </div>

                  {/* SECURITY SECTION */}
                  <div>
                    <h4 style={{ color: 'var(--accent-gold)', marginBottom: '15px', fontSize: '1.1rem', fontFamily: 'Cinzel, serif', borderBottom: '1px dashed var(--muted-gold)', paddingBottom: '8px' }}>Security</h4>
                    <p style={{fontSize: '0.9rem', opacity: 0.8, marginBottom: '15px', color: 'var(--sidebar-text)', fontFamily: 'Courier Prime, monospace'}}>Fortify your vault with a new passphrase.</p>
                    
                    {!showPasswordInput ? (
                      <button 
                        className="wax-seal-btn" 
                        style={{padding: '12px 20px', fontSize: '1rem', width: '100%', borderRadius: '4px', backgroundColor: 'transparent', border: '1px solid var(--accent-gold)', color: 'var(--accent-gold)', boxShadow: 'none'}} 
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.1)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--accent-gold)'; }}
                        onClick={() => setShowPasswordInput(true)}
                      >
                        Change Password
                      </button>
                    ) : (
                      <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                        <input type="password" placeholder="New Password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="vintage-input" style={{width: '100%', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid var(--accent-gold)', padding: '12px', color: '#fff'}} />
                        <div style={{display: 'flex', gap: '10px'}}>
                          <button className="wax-seal-btn" style={{flex: 1, padding: '10px', borderRadius: '4px', fontSize: '1rem'}} onClick={() => handlePasswordUpdate(newPassword)}>Update</button>
                          <button style={{background: 'rgba(255,255,255,0.05)', border: '1px solid var(--faded-line)', color: 'var(--sidebar-text)', cursor: 'pointer', borderRadius: '4px', padding: '10px 20px', fontFamily: 'Courier Prime, monospace'}} onClick={() => {setShowPasswordInput(false); setNewPassword("");}}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>

                </div>

                {/* SAĞ KOLON: ÖNCELİKLER */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <h4 style={{ color: 'var(--accent-gold)', marginBottom: '15px', fontSize: '1.1rem', fontFamily: 'Cinzel, serif', borderBottom: '1px dashed var(--muted-gold)', paddingBottom: '8px' }}>Priority Calibration</h4>
                  <p style={{fontSize: '0.9rem', opacity: 0.8, marginBottom: '15px', color: 'var(--sidebar-text)', fontFamily: 'Courier Prime, monospace'}}>Drag and drop to re-rank your core multipliers. Changes will affect XP yields.</p>
                  
                  <div style={{flex: 1}}>
                    <DraggableRankList ranks={settingsRanks} setRanks={setSettingsRanks} />
                  </div>

                  <button 
                    className="wax-seal-btn"
                    style={{width: '100%', textAlign: 'center',display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px', fontSize: '1.1rem', borderRadius: '4px', marginTop: '15px'}}
                    onClick={saveSettingsPriorities}
                  >
                    Save Priorities
                  </button>
                </div>
              </div>

              {/* DANGER ZONE (TÜM GENİŞLİK - EN ALT) */}
              <div style={{ borderTop: '2px solid var(--accent-red)', paddingTop: '25px', marginTop: '40px', backgroundColor: 'rgba(122, 19, 24, 0.05)', padding: '25px', borderRadius: '4px', border: '1px solid rgba(122, 19, 24, 0.2)' }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px'}}>
                  <div style={{flex: 1, minWidth: '250px'}}>
                    <h4 style={{color: '#ff6b6b', marginBottom: '8px', fontSize: '1.2rem', fontFamily: 'Cinzel, serif', display: 'flex', alignItems: 'center', gap: '10px'}}>
                      <span>⚠️</span> Danger Zone
                    </h4>
                    <p style={{fontSize: '0.9rem', color: '#ff6b6b', opacity: 0.8, fontFamily: 'Courier Prime, monospace', margin: 0}}>
                      This action is irreversible. To erase your existence, type <b>DELETE</b>.
                    </p>
                  </div>
                  
                  <div style={{display: 'flex', gap: '15px', alignItems: 'center', flex: 1, minWidth: '300px'}}>
                    <input 
                      className="vintage-input" 
                      placeholder="Type DELETE" 
                      value={wipeConfirmation}
                      onChange={(e) => setWipeConfirmation(e.target.value)}
                      style={{flex: 1, textAlign: 'center', backgroundColor: 'rgba(0,0,0,0.4)', border: `1px solid ${wipeConfirmation === "DELETE" ? '#ff6b6b' : 'var(--faded-line)'}`, color: '#fff', borderRadius: '4px', padding: '12px'}} 
                    />

                    <button 
                      disabled={wipeConfirmation !== "DELETE"}
                      style={{
                        backgroundColor: wipeConfirmation === "DELETE" ? 'var(--accent-red)' : 'rgba(0,0,0,0.5)',
                        color: wipeConfirmation === "DELETE" ? '#fff' : 'var(--muted-gold)',
                        border: wipeConfirmation === "DELETE" ? '1px solid #3d0708' : '1px solid var(--faded-line)',
                        padding: '12px 25px', borderRadius: '4px', 
                        cursor: wipeConfirmation === "DELETE" ? 'pointer' : 'not-allowed', 
                        fontSize: '1rem', fontWeight: 'bold', transition: 'all 0.2s', fontFamily: 'Cinzel, serif'
                      }}
                      onClick={() => {
                        if(wipeConfirmation === "DELETE") {
                          showConfirm("Are you certain you wish to wipe your legacy? There is no return.", () => {
                            setPlayer({...defaultState}); 
                            setShowSettings(false);
                            setWipeConfirmation("");
                            showAlert("Your legacy has been erased.", "Void");
                          }, "Erase Legacy");
                        }
                      }}
                    >
                      WIPE DATA
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}

      </main>
    </div>
  );
};

const MainContent = () => {
  const { user } = useAuth();
  const [showAuth, setShowAuth] = useState(false);

  React.useEffect(() => {
    if (!user) {
      setShowAuth(false); 
    }
  }, [user]);

  if (user) return <Dashboard />;
  if (showAuth) return <AuthGate onBack={() => setShowAuth(false)} />;
  return <LandingPage onLoginClick={() => setShowAuth(true)} />;
};

function App() {
  return (
    <AuthProvider>
      <DialogProvider>
        <MainContent />
      </DialogProvider>
    </AuthProvider>
  );
}

export default App;